import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'
import FichaOficina from '../components/FichaOficina'

// Componente separado para evitar re-renders
const VACIO = { nombre: '', apellido: '', email: '', email_2: '', telefono: '' }

function ContactosPanel({ of, isAdmin, isMobile }) {
  const [contactos, setContactos] = useState([])
  const [newContacto, setNewContacto] = useState(VACIO)
  const [editId, setEditId] = useState(null)
  const [editData, setEditData] = useState(VACIO)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchContactos()
  }, [of.id])

  const fetchContactos = async () => {
    const { data } = await supabase.from('contactos_oficina').select('*')
      .eq('oficina_id', of.id).eq('activo', true).order('apellido')
    setContactos(data || [])
  }

  const limpiar = (c) => ({
    nombre: c.nombre.trim(),
    apellido: c.apellido.trim(),
    email: c.email.trim().toLowerCase() || null,
    email_2: c.email_2.trim().toLowerCase() || null,
    telefono: c.telefono.trim() || null,
  })

  const handleAdd = async () => {
    if (!newContacto.nombre.trim() || !newContacto.apellido.trim()) return
    setLoading(true)
    const { error } = await supabase.from('contactos_oficina').insert({
      ...limpiar(newContacto),
      oficina_id: of.id,
      activo: true
    })
    if (error) {
      console.error('Error al agregar contacto:', error)
      alert(`❌ Error: ${error.message}`)
      setLoading(false)
      return
    }
    setNewContacto(VACIO)
    await fetchContactos()
    setLoading(false)
  }

  const startEdit = (c) => {
    setEditId(c.id)
    setEditData({
      nombre: c.nombre || '', apellido: c.apellido || '',
      email: c.email || '', email_2: c.email_2 || '', telefono: c.telefono || ''
    })
  }

  const handleSaveEdit = async () => {
    if (!editData.nombre.trim() || !editData.apellido.trim()) return
    setLoading(true)
    const { data, error } = await supabase.from('contactos_oficina')
      .update(limpiar(editData)).eq('id', editId).select()
    if (error || !data?.length) {
      console.error('Error al editar contacto:', error)
      alert(`❌ No se pudo guardar: ${error?.message || 'sin permisos para editar'}`)
      setLoading(false)
      return
    }
    setEditId(null)
    await fetchContactos()
    setLoading(false)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este contacto?')) return
    await supabase.from('contactos_oficina').update({ activo: false }).eq('id', id)
    fetchContactos()
  }

  const inputStyle = { fontSize: 13, padding: '8px 12px' }
  const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 15, padding: '2px 8px' }

  const campos = (data, setData) => (
    <>
      <input className="input" placeholder="Nombre *" value={data.nombre}
        onChange={e => setData(p => ({ ...p, nombre: e.target.value }))} style={inputStyle} />
      <input className="input" placeholder="Apellido *" value={data.apellido}
        onChange={e => setData(p => ({ ...p, apellido: e.target.value }))} style={inputStyle} />
      <input className="input" placeholder="Email" value={data.email}
        onChange={e => setData(p => ({ ...p, email: e.target.value }))} style={inputStyle} />
      <input className="input" placeholder="Email 2 (ej. asistente)" value={data.email_2}
        onChange={e => setData(p => ({ ...p, email_2: e.target.value }))} style={inputStyle} />
      <input className="input" placeholder="Teléfono" value={data.telefono}
        onChange={e => setData(p => ({ ...p, telefono: e.target.value }))} style={inputStyle} />
    </>
  )

  const grid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr 1fr auto', gap: 8 }

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: 10, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 10 }}>
        CONTACTOS · {contactos.length} persona{contactos.length !== 1 ? 's' : ''}
      </div>

      {contactos.map(c => editId === c.id ? (
        <div key={c.id} style={{ ...grid, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
          {campos(editData, setEditData)}
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-gold btn-sm" onClick={handleSaveEdit}
              disabled={loading || !editData.nombre.trim() || !editData.apellido.trim()}>Guardar</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditId(null)}>✕</button>
          </div>
        </div>
      ) : (
        <div key={c.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{c.nombre} {c.apellido}</div>
            {(c.email || c.email_2 || c.telefono) && (
              <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
                {[c.email, c.email_2, c.telefono].filter(Boolean).join(' · ')}
              </div>
            )}
          </div>
          <div style={{ display: 'flex' }}>
            <button title="Editar" onClick={() => startEdit(c)} style={iconBtn}>✎</button>
            <button title="Eliminar" onClick={() => handleDelete(c.id)} style={iconBtn}>✕</button>
          </div>
        </div>
      ))}

      <div style={{ ...grid, marginTop: 12 }}>
        {campos(newContacto, setNewContacto)}
        <button className="btn btn-gold btn-sm" onClick={handleAdd}
          disabled={loading || !newContacto.nombre.trim() || !newContacto.apellido.trim()}>
          + Agregar
        </button>
      </div>
    </div>
  )
}

export default function Oficinas() {
  const { usuario } = useAuth()
  const [oficinas, setOficinas] = useState([])
  const [edificios, setEdificios] = useState([])
  const [filtroEdificio, setFiltroEdificio] = useState('all')
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [newOficina, setNewOficina] = useState({
    edificio_id: '',
    numero: '',
    titular: '',
    email_contacto: '',
    email_contacto_2: '',
    telefono: ''
  })
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [showContactos, setShowContactos] = useState(null)
  const [fichaOficina, setFichaOficina] = useState(null)

  const isAdmin = usuario?.rol === 'admin'
  const edificioId = isAdmin ? null : usuario?.edificio_id

  useEffect(() => {
    fetchEdificios()
    fetchOficinas()

    const handleResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    fetchOficinas()
  }, [filtroEdificio])

  const fetchEdificios = async () => {
    let q = supabase.from('edificios').select('*')

    if (edificioId) q = q.eq('id', edificioId)

    const { data } = await q
    setEdificios(data || [])
  }

  const fetchOficinas = async () => {
    let q = supabase.from('oficinas').select('*, edificios(nombre)').order('numero')

    if (edificioId) q = q.eq('edificio_id', edificioId)
    else if (filtroEdificio !== 'all') q = q.eq('edificio_id', filtroEdificio)

    const { data } = await q
    setOficinas(data || [])
  }

  const handleShowContactos = (id) => {
    setShowContactos(prev => prev === id ? null : id)
  }

  const handleEdit = (of) => {
    setEditingId(of.id)
    setEditForm({
      titular: of.titular,
      email_contacto: of.email_contacto || '',
      email_contacto_2: of.email_contacto_2 || '',
      telefono: of.telefono || ''
    })
  }

  const handleSave = async () => {
    setLoading(true)

    await supabase.from('oficinas').update(editForm).eq('id', editingId)

    setEditingId(null)
    showSuccessMsg('Oficina actualizada')
    fetchOficinas()
    setLoading(false)
  }

  const handleAddOficina = async () => {
    if (!newOficina.edificio_id || !newOficina.numero || !newOficina.titular) return

    setLoading(true)

    await supabase.from('oficinas').insert({ ...newOficina, activa: true })

    setNewOficina({
      edificio_id: '',
      numero: '',
      titular: '',
      email_contacto: '',
      email_contacto_2: '',
      telefono: ''
    })

    setShowForm(false)
    showSuccessMsg('Oficina agregada correctamente')
    fetchOficinas()
    setLoading(false)
  }

  const showSuccessMsg = (msg) => {
    setSuccess(msg)
    setTimeout(() => setSuccess(null), 2500)
  }

  return (
    <div className="fade-in">
      {success && (
        <div
          style={{
            position: 'fixed',
            top: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--green)',
            color: 'white',
            padding: '14px 28px',
            borderRadius: 10,
            fontWeight: 600,
            fontSize: 14,
            zIndex: 9999,
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
            animation: 'slideDown 0.3s ease'
          }}
        >
          ✓ {success}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <h1 style={{ fontSize: 36, color: 'var(--text)', marginBottom: 4 }}>Oficinas</h1>
          <p style={{ color: 'var(--text3)', fontSize: 14 }}>
            {edificioId ? usuario?.edificios?.nombre : 'Todos los edificios'} · {oficinas.length} unidades
          </p>
        </div>

        {isAdmin && (
          <button className="btn btn-gold" onClick={() => setShowForm(!showForm)}>
            {showForm ? '✕' : '+ Nueva'}
          </button>
        )}
      </div>

      {isAdmin && showForm && (
        <div className="card fade-in" style={{ marginBottom: 24, borderColor: 'var(--gold)', borderWidth: 1.5 }}>
          <div style={{ fontSize: 11, color: 'var(--gold)', fontFamily: 'DM Mono, monospace', letterSpacing: 1, marginBottom: 20 }}>
            NUEVA OFICINA
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>EDIFICIO</label>
              <select
                className="input"
                value={newOficina.edificio_id}
                onChange={e => setNewOficina(f => ({ ...f, edificio_id: e.target.value }))}
              >
                <option value="">Seleccioná edificio...</option>
                {edificios.map(e => (
                  <option key={e.id} value={e.id}>{e.nombre}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>NÚMERO</label>
              <input
                className="input"
                placeholder="Ej: 9A"
                value={newOficina.numero}
                onChange={e => setNewOficina(f => ({ ...f, numero: e.target.value }))}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>TITULAR</label>
              <input
                className="input"
                placeholder="Nombre del titular"
                value={newOficina.titular}
                onChange={e => setNewOficina(f => ({ ...f, titular: e.target.value }))}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>EMAIL GENERAL</label>
              <input
                className="input"
                placeholder="email@ejemplo.com"
                value={newOficina.email_contacto}
                onChange={e => setNewOficina(f => ({ ...f, email_contacto: e.target.value }))}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>EMAIL GENERAL 2</label>
              <input
                className="input"
                placeholder="Opcional"
                value={newOficina.email_contacto_2}
                onChange={e => setNewOficina(f => ({ ...f, email_contacto_2: e.target.value }))}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>TELÉFONO</label>
              <input
                className="input"
                placeholder="Opcional"
                value={newOficina.telefono}
                onChange={e => setNewOficina(f => ({ ...f, telefono: e.target.value }))}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-outline btn-sm" onClick={() => setShowForm(false)}>
              Cancelar
            </button>

            <button
              className="btn btn-gold btn-sm"
              onClick={handleAddOficina}
              disabled={loading || !newOficina.edificio_id || !newOficina.numero || !newOficina.titular}
            >
              Agregar
            </button>
          </div>
        </div>
      )}

      {isAdmin && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          <button
            className={`btn btn-sm ${filtroEdificio === 'all' ? 'btn-gold' : 'btn-ghost'}`}
            onClick={() => setFiltroEdificio('all')}
          >
            Todos
          </button>

          {edificios.map(e => (
            <button
              key={e.id}
              className={`btn btn-sm ${filtroEdificio === e.id ? 'btn-gold' : 'btn-ghost'}`}
              onClick={() => setFiltroEdificio(e.id)}
            >
              {e.nombre.replace('Essence ', '')}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {oficinas.map((of) => (
          <div key={of.id} className="card" style={{ padding: isMobile ? '14px 16px' : '16px 20px' }}>
            {editingId === of.id ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, color: 'var(--gold)', fontWeight: 700 }}>
                    {of.numero}
                  </span>

                  {isAdmin && (
                    <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                      {of.edificios?.nombre?.replace('Essence ', '')}
                    </span>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>TITULAR</label>
                    <input
                      className="input"
                      value={editForm.titular}
                      onChange={e => setEditForm(f => ({ ...f, titular: e.target.value }))}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>EMAIL GENERAL</label>
                    <input
                      className="input"
                      value={editForm.email_contacto}
                      onChange={e => setEditForm(f => ({ ...f, email_contacto: e.target.value }))}
                      placeholder="email@ejemplo.com"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>EMAIL GENERAL 2</label>
                    <input
                      className="input"
                      value={editForm.email_contacto_2}
                      onChange={e => setEditForm(f => ({ ...f, email_contacto_2: e.target.value }))}
                      placeholder="Opcional"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>TELÉFONO</label>
                    <input
                      className="input"
                      value={editForm.telefono}
                      onChange={e => setEditForm(f => ({ ...f, telefono: e.target.value }))}
                      placeholder="Opcional"
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-outline btn-sm" onClick={() => setEditingId(null)}>
                    Cancelar
                  </button>

                  <button className="btn btn-gold btn-sm" onClick={handleSave} disabled={loading}>
                    Guardar
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2, flexWrap: 'wrap' }}>
                      <span
                        onClick={() => setFichaOficina(of)}
                        style={{
                          fontFamily: 'DM Mono, monospace',
                          fontSize: 13,
                          color: 'var(--gold)',
                          fontWeight: 700,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationStyle: 'dotted',
                          textUnderlineOffset: 3,
                        }}
                        onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
                        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                      >
                        {of.numero}
                      </span>

                      {isAdmin && (
                        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                          {of.edificios?.nombre?.replace('Essence ', '')}
                        </span>
                      )}

                      {!isMobile && of.email_contacto && (
                        <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
                          {of.email_contacto}
                        </span>
                      )}

                      {!isMobile && of.email_contacto_2 && (
                        <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>
                          {of.email_contacto_2}
                        </span>
                      )}
                    </div>

                    <div style={{ fontSize: 14, fontWeight: 500 }}>{of.titular}</div>
                  </div>

                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(of)}>
                      Editar
                    </button>

                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: showContactos === of.id ? 'var(--gold)' : 'var(--text3)' }}
                      onClick={() => handleShowContactos(of.id)}
                    >
                      👥
                    </button>
                  </div>
                </div>

                {showContactos === of.id && (
                  <ContactosPanel of={of} isAdmin={isAdmin} isMobile={isMobile} />
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {fichaOficina && (
        <FichaOficina oficina={fichaOficina} onClose={() => setFichaOficina(null)} />
      )}
    </div>
  )
}